import { useEffect, useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel } from "../labels";

function toIsoUtc(local: string): string | undefined {
  if (!local.trim()) return undefined;
  const date = new Date(local);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function ConfirmActionDialog({
  open,
  title,
  notice,
  actions,
  requireReason = true,
  askApprovalId = false,
  askScheduledAt = false,
  confirmVariant = "primary",
  extraFields,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  requireReason?: boolean;
  askApprovalId?: boolean;
  askScheduledAt?: boolean;
  confirmVariant?: "primary" | "danger";
  extraFields?: ReactNode;
  onClose: () => void;
  onSubmit: (input: {
    action: string;
    reason?: string;
    approval_id?: string;
    scheduled_at?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const first = actions[0] ?? "";
  const [action, setAction] = useState(first);
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction(first);
    setReason("");
    setApprovalId("");
    setScheduledAt("");
  }, [open, first]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            loading={busy}
            disabled={
              !action || (requireReason && reason.trim().length < 1)
            }
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
                ...(askScheduledAt
                  ? {
                      scheduled_at: scheduledAt.trim()
                        ? (toIsoUtc(scheduledAt) ?? null)
                        : null,
                    }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          options={actions.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        {askApprovalId ? (
          <ApprovalPickerField
            label="Approved Approval (optional)"
            value={approvalId}
            onChange={setApprovalId}
          />
        ) : null}
        {askScheduledAt ? (
          <Input
            label="Scheduled at (optional)"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        ) : null}
        {extraFields}
        <Textarea
          label={requireReason ? "Reason (required)" : "Reason (optional)"}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
        />
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
